"use client"

import React, { useState, useEffect, useRef } from "react"
import {
    Bar, BarChart, CartesianGrid, LabelList,
    XAxis, YAxis, Legend, Tooltip, ResponsiveContainer
} from "recharts"
import { CircleHelp, Download, ChevronDown } from "lucide-react"
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"
import axiosInstance from "@/api/axios"
import { ChartContainer } from "@/components/ui/chart"
import { Button } from "@/components/ui/button"
import {
    DropdownMenu, DropdownMenuContent,
    DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useTranslation } from "react-i18next"
import { getInvertedColorScheme } from "@/lib/colorSchemes"

interface WasteStreamEntry {
    waste_stream: string
    contamination_rate: number
    contaminated_items: number
    total_items: number
    contaminated_weight_kg: number
    total_weight_kg: number
    contamination_rate_by_weight: number
    contaminated_subclasses?: { name: string; count: number; weight_kg?: number }[]
}

interface PerBinData {
    bin: string
    total_items: number
    contaminated_items: number
    contamination_rate: number
    total_weight_kg: number
    contaminated_weight_kg: number
    contamination_rate_by_weight: number
    waste_streams: WasteStreamEntry[]
}

interface ApiResponse {
    per_bin_data: PerBinData[]
    contamination_rates: WasteStreamEntry[]
    overall_contamination_rate: number
    total_weight_kg: number
    total_contaminated_weight_kg: number
    overall_contamination_rate_by_weight: number
}

interface ContaminationRateChartProps extends React.HTMLAttributes<HTMLDivElement> {
    className?: string
    title?: string
    bins?: string[]
    binTypes?: Record<string, { country?: string }>
    timeRange?: string
    customDateRange?: Array<{
        startDate: Date | null
        endDate: Date | null
        key: string
    }>
    isCustomDateRange?: boolean
}

// Viewer's browser timezone. Sent to the backend (?tz=) so this chart uses the
// same hours/days as every other card.
const TZ = (typeof Intl !== "undefined" && Intl.DateTimeFormat().resolvedOptions().timeZone) || "Europe/Amsterdam"

const STREAMS = ["Organic", "PMD", "Paper", "Residual"] as const
type Stream = typeof STREAMS[number]

const normalise = (ws: string): Stream | null => {
    const l = ws.toLowerCase()
    if (l === "organic")               return "Organic"
    if (l === "pmd" || l === "plastic") return "PMD"
    if (l === "paper")                 return "Paper"
    if (l === "residual" || l === "rest") return "Residual"
    return null
}

export function ContaminationRateChart({
    className,
    title,
    bins = [],
    binTypes = {},
    timeRange,
    customDateRange = [{ startDate: null, endDate: null, key: "selection" }],
    isCustomDateRange = false,
    ...props
}: ContaminationRateChartProps) {
    const exportRef = useRef<HTMLDivElement>(null)
    const chartWrapperRef = useRef<HTMLDivElement>(null)
    const { t } = useTranslation()
    const [apiData, setApiData]   = useState<ApiResponse | null>(null)
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError]       = useState<string | null>(null)
    const [showKg, setShowKg]     = useState(false)   // false = %, true = kg
    const [containerWidth, setContainerWidth] = useState(0)

    // Track chart wrapper width so labels/margins can scale down on narrow screens
    useEffect(() => {
        if (!chartWrapperRef.current) return
        const ro = new ResizeObserver(([entry]) => setContainerWidth(entry.contentRect.width))
        ro.observe(chartWrapperRef.current)
        return () => ro.disconnect()
    }, [])

    // This chart intentionally uses the INVERTED palette relative to the
    // rest of the dashboard — see getInvertedColorScheme.
    const streamColors = React.useMemo(() => {
        const scheme = getInvertedColorScheme(bins, binTypes)
        return {
            Organic:  scheme.Organic,
            PMD:      scheme.PMD,
            Paper:    scheme.Paper,
            Residual: scheme.Residual,
        }
    }, [bins, binTypes])

    const chartConfig = React.useMemo(() => ({
        Organic:  { label: t('contaminationRateChart.wasteTypes.Organic'),  color: streamColors.Organic },
        PMD:      { label: t('contaminationRateChart.wasteTypes.PMD'),      color: streamColors.PMD },
        Paper:    { label: t('contaminationRateChart.wasteTypes.Paper'),    color: streamColors.Paper },
        Residual: { label: t('contaminationRateChart.wasteTypes.Residual'), color: streamColors.Residual },
    }), [t, streamColors])

    useEffect(() => {
        console.log("ContaminationRateChart fetch triggered", {
            isCustomDateRange,
            start: customDateRange?.[0]?.startDate,
            end: customDateRange?.[0]?.endDate,
        })

        if (!bins || bins.length === 0) return
        if (!timeRange && !isCustomDateRange) return

        setIsLoading(true)
        setError(null)
        setApiData(null)

        const params = new URLSearchParams()
        bins.forEach(b => params.append("bin", b))

        if (isCustomDateRange && customDateRange[0]?.startDate && customDateRange[0]?.endDate) {
            const s = customDateRange[0].startDate instanceof Date ? customDateRange[0].startDate : new Date(customDateRange[0].startDate)
            const e = customDateRange[0].endDate instanceof Date ? customDateRange[0].endDate : new Date(customDateRange[0].endDate)
            // Same format as the other cards: full ISO instants. The backend
            // converts them to the viewer's timezone before picking the day.
            params.set("start_date", s.toISOString())
            params.set("end_date",   e.toISOString())
        } else {
            params.set("period", (timeRange || "year").replace(/\s+/g, "").toLowerCase())
        }
        params.set("tz", TZ)

        axiosInstance
            .get(`/contamination-rates/?${params.toString()}`)
            .then(res => {
                if (res.data?.per_bin_data) setApiData(res.data)
                else setError(t('contaminationRateChart.errors.invalidDataFormat'))
            })
            .catch(() => setError(t('contaminationRateChart.errors.failedToLoad')))
            .finally(() => setIsLoading(false))
    }, [
        timeRange,
        isCustomDateRange,
        JSON.stringify(bins),
        customDateRange?.[0]?.startDate instanceof Date
            ? customDateRange[0].startDate.toDateString()
            : String(customDateRange?.[0]?.startDate ?? ""),
        customDateRange?.[0]?.endDate instanceof Date
            ? customDateRange[0].endDate.toDateString()
            : String(customDateRange?.[0]?.endDate ?? ""),
    ])

    // NOTE: in % mode, each stream's value is now that stream's CONTRIBUTION
    // to the bin's overall contamination rate (contaminated_items / bin total_items),
    // not the stream's own internal contamination rate. This makes the stacked
    // segments sum to the bin's real overall rate, so total bar length is meaningful.
    const chartRows = React.useMemo(() => {
        if (!apiData?.per_bin_data?.length) return []

        return apiData.per_bin_data.map(bin => {
            const row: Record<string, any> = { bin: bin.bin }

            STREAMS.forEach(stream => {
                const ws = bin.waste_streams.find(w => normalise(w.waste_stream) === stream)
                row[stream] = ws
                    ? showKg
                        ? (ws.contaminated_weight_kg ?? 0)
                        : bin.total_items > 0
                            ? ((ws.contaminated_items ?? 0) / bin.total_items) * 100
                            : 0
                    : 0
            })

            row.__track                    = 0   // zero-width bar that carries the track + total label
            row.__total_items              = bin.total_items
            row.__contaminated_items       = bin.contaminated_items
            row.__contamination_rate       = bin.contamination_rate
            row.__total_weight_kg          = bin.total_weight_kg
            row.__contaminated_weight_kg   = bin.contaminated_weight_kg

            return row
        })
    }, [apiData, showKg])

    const activeStreams = STREAMS.filter(s =>
        chartRows.some(r => (r[s] as number) > 0)
    )

    const hasWeightData = !!(apiData?.total_weight_kg && apiData.total_weight_kg > 0)

    // There is waste, but none of it is contaminated: still draw every bin's
    // (empty) bar track and say so, instead of looking like "no data".
    const allClean = chartRows.length > 0 && activeStreams.length === 0

    // Responsive label sizing — mirrors the xAxisInterval breakpoint pattern
    // used in ContaminationTime.jsx
    const labelFontSize    = containerWidth < 340 ? 10 : containerWidth < 480 ? 11 : 12
    const labelOffset      = containerWidth < 340 ? 4  : 8
    const chartMarginRight = containerWidth < 340 ? 36 : containerWidth < 480 ? 55 : 70

    const CustomTooltip = ({ active, payload, label }: any) => {
        if (!active || !payload?.length) return null
        const bin = apiData?.per_bin_data.find(b => b.bin === label)

        return (
            <div className="rounded-lg border bg-background p-3 shadow-md text-sm min-w-[190px]">
                <p className="font-semibold mb-2">{label}</p>
                <div className="space-y-1">
                    {payload
                        .filter((e: any) => Number(e.value) > 0)
                        .map((entry: any, i: number) => (
                            <div key={i} className="flex items-center justify-between gap-4">
                                <div className="flex items-center gap-1.5">
                                    <div
                                        className="h-2.5 w-2.5 rounded-sm flex-shrink-0"
                                        style={{ backgroundColor: entry.fill }}
                                    />
                                    <span className="text-muted-foreground">{entry.name}</span>
                                </div>
                                <span className="font-medium">
                                    {showKg
                                        ? `${Number(entry.value).toFixed(2)} kg`
                                        : `${Number(entry.value).toFixed(1)}%`}
                                </span>
                            </div>
                        ))}
                </div>
                {bin && (
                    <div className="border-t mt-2 pt-2 text-xs text-muted-foreground space-y-0.5">
                        <div>
                            {t('contaminationRateChart.tooltip.totalItems')}:{" "}
                            <span className="font-medium text-foreground">{bin.total_items}</span>
                        </div>
                        <div>
                            {t('contaminationRateChart.tooltip.contaminated')}:{" "}
                            <span className="font-medium text-foreground">{bin.contaminated_items}</span>
                        </div>
                        <div>
                            {t('contaminationRateChart.tooltip.overallRate')}:{" "}
                            <span className="font-medium text-foreground">
                                {showKg && bin.total_weight_kg > 0
                                    ? `${bin.contamination_rate_by_weight?.toFixed(1) ?? "—"}%`
                                    : `${bin.contamination_rate.toFixed(1)}%`}
                            </span>
                        </div>
                        {showKg && bin.total_weight_kg > 0 && (
                            <div>
                                {t('contaminationRateChart.tooltip.totalWeight')}:{" "}
                                <span className="font-medium text-foreground">
                                    {bin.total_weight_kg.toFixed(2)} kg
                                </span>
                            </div>
                        )}
                    </div>
                )}
            </div>
        )
    }

    const handleExportCSV = () => {
        if (!apiData?.per_bin_data) return
        const headers = [
            "Bin",
            ...STREAMS.map(s => `${s} Contamination (%)`),
            ...STREAMS.map(s => `${s} Contaminated Weight (kg)`),
            "Total Items",
            "Contaminated Items",
            "Overall Contamination Rate (%)",
            "Correctly Sorted",
        ]
        const rows = apiData.per_bin_data.map(bin => {
            const pct = STREAMS.map(s => {
                const ws = bin.waste_streams.find(w => normalise(w.waste_stream) === s)
                return ws ? ws.contamination_rate.toFixed(2) : "0"
            })
            const kg = STREAMS.map(s => {
                const ws = bin.waste_streams.find(w => normalise(w.waste_stream) === s)
                return ws ? (ws.contaminated_weight_kg ?? 0).toFixed(3) : "0"
            })

            const correctlySorted = bin.contamination_rate === 0
                ? "Yes"
                : bin.contamination_rate === 100
                    ? "No"
                    : `Partial (${(100 - bin.contamination_rate).toFixed(1)}% sorted correctly)`

            return [
                bin.bin,
                ...pct,
                ...kg,
                bin.total_items,
                bin.contaminated_items,
                bin.contamination_rate.toFixed(2),
                correctlySorted,
            ].join(",")
        })
        const csv = [headers.join(","), ...rows].join("\n")
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
        const link = document.createElement("a")
        link.href = URL.createObjectURL(blob)
        link.download = `contamination_comparison_${new Date().toISOString().slice(0, 10)}.csv`
        link.click()
    }

    const handleExportPNG = () => {
        if (!exportRef.current) return
        const controls = exportRef.current.querySelector(".export-controls") as HTMLElement | null
        if (controls) controls.style.display = "none"
        import("html2canvas").then(h => {
            h.default(exportRef.current!, { scale: 2, useCORS: true }).then(canvas => {
                const link = document.createElement("a")
                link.download = `contamination_comparison_${timeRange || "data"}.png`
                link.href = canvas.toDataURL("image/png")
                link.click()
            }).finally(() => { if (controls) controls.style.display = "" })
        })
    }

    const barSize    = Math.min(34, Math.max(16, 180 / Math.max(chartRows.length, 1)))
    const chartHeight = Math.max(260, chartRows.length * (barSize + 22) + 70)

    return (
        <div
            ref={exportRef}
            className={cn(
                "lg:col-span-2 aspect-[1/1] sm:aspect-[16/10] sm:rounded-lg bg-muted/50 p-4 exportable-section border-0 shadow-none",
                className
            )}
            {...props}
        >
            <div className="flex flex-wrap justify-between items-start gap-2 pb-2">
                <div>
                    <h4 className="scroll-m-20 text-xl font-semibold tracking-tight">
                        {title || t('contaminationRateChart.defaultTitle')}
                    </h4>
                    {allClean && !isLoading && (
                        <p className="text-sm text-green-600 dark:text-green-400 mt-0.5">
                            {t('contaminationRateChart.states.noContamination', 'No contamination in this period')}
                        </p>
                    )}
                    {isCustomDateRange &&
                        customDateRange[0]?.startDate &&
                        customDateRange[0]?.endDate && (
                            <p className="text-sm text-muted-foreground mt-0.5">
                                {new Date(customDateRange[0].startDate!).toLocaleDateString()} –{" "}
                                {new Date(customDateRange[0].endDate!).toLocaleDateString()}
                            </p>
                        )}
                </div>

                <div className="flex items-center gap-2 export-controls">
                <div className="flex items-center gap-1 bg-gray-100 dark:bg-slate-800 rounded-lg p-1 border border-gray-200 dark:border-slate-700">
                    <button
                        onClick={() => setShowKg(true)}
                        disabled={!hasWeightData}
                        className={cn(
                            "px-3 py-1 text-xs font-bold rounded-md transition-all",
                            showKg
                                ? "bg-white dark:bg-slate-600 text-primary shadow-sm"
                                : "text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200",
                            !hasWeightData && "opacity-40 cursor-not-allowed"
                        )}
                    >
                        KG
                    </button>
                    <button
                        onClick={() => setShowKg(false)}
                        className={cn(
                            "px-3 py-1 text-xs font-bold rounded-md transition-all",
                            !showKg
                                ? "bg-white dark:bg-slate-600 text-primary shadow-sm"
                                : "text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                        )}
                    >
                        %
                    </button>
                </div>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="text-muted-foreground hover:text-primary"
                                disabled={!apiData || isLoading}
                            >
                                <Download className="h-4 w-4 mr-1" />
                                {t('contaminationRateChart.export.export')}
                                <ChevronDown className="h-4 w-4 ml-1" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={handleExportCSV}>{t('contaminationRateChart.export.downloadCSV')}</DropdownMenuItem>
                            <DropdownMenuItem onClick={handleExportPNG}>{t('contaminationRateChart.export.downloadPNG')}</DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>

                    <TooltipProvider>
                        <UITooltip>
                            <TooltipTrigger asChild>
                                <CircleHelp className="h-5 w-5 text-muted-foreground cursor-help" />
                            </TooltipTrigger>
                            <TooltipContent side="left" className="max-w-[220px]">
                                <p dangerouslySetInnerHTML={{ __html: t('contaminationRateChart.help.description') }} />
                            </TooltipContent>
                        </UITooltip>
                    </TooltipProvider>
                </div>
            </div>

            <div className="px-1 pt-2 sm:px-2 sm:pt-2" style={{ width: '100%', height: '100%' }} ref={chartWrapperRef}>
                {isLoading ? (
                    // Skeleton — a handful of rows, each a bin's stacked
                    // horizontal bar split into contamination-by-stream
                    // segments at varying total lengths (not 100%-stacked
                    // like WasteComposition — bars here vary in overall
                    // length since they represent each bin's own rate),
                    // with a trailing total-rate label placeholder.
                    <div className="flex flex-col justify-around h-full w-full py-2 motion-safe:animate-pulse" aria-busy="true" style={{ paddingRight: chartMarginRight }}>
                        <span className="sr-only" role="status">{t('contaminationRateChart.loading', 'Loading…')}</span>
                        {[
                            { total: 70, segments: [30, 20, 10, 10] },
                            { total: 40, segments: [15, 15, 10] },
                            { total: 85, segments: [35, 25, 15, 10] },
                            { total: 25, segments: [10, 15] },
                        ].map((row, i) => (
                            <div key={i} className="flex items-center gap-2">
                                <div className="w-10 h-3 rounded bg-gray-200 dark:bg-slate-700 flex-shrink-0" />
                                <div className="flex-1 flex rounded overflow-hidden" style={{ height: `${barSize}px` }}>
                                    <div className="flex" style={{ width: `${row.total}%` }}>
                                        {row.segments.map((w, si) => (
                                            <div
                                                key={si}
                                                className="h-full bg-gray-200 dark:bg-slate-700"
                                                style={{
                                                    width: `${(w / row.total) * 100}%`,
                                                    opacity: 0.5 + (si % 4) * 0.12,
                                                    borderRight: si < row.segments.length - 1 ? '1px solid rgba(255,255,255,0.4)' : 'none',
                                                }}
                                            />
                                        ))}
                                    </div>
                                </div>
                                <div className="h-3 w-10 rounded bg-gray-200 dark:bg-slate-700 flex-shrink-0" />
                            </div>
                        ))}
                    </div>
                ) : error ? (
                    <div className="flex items-center justify-center h-full text-destructive">
                        {error}
                    </div>
                ) : chartRows.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-[400px]">
                        <div className="text-muted-foreground text-center">
                            <p className="mb-2 text-lg">{t('contaminationRateChart.states.noDataAvailable')}</p>
                            <p className="text-sm">{t('contaminationRateChart.states.selectTimeRangeAndBins')}</p>
                        </div>
                    </div>
                ) : (
                    <ChartContainer config={chartConfig} className="h-full w-full">
                        <ResponsiveContainer width="100%" height={100}>
                            <BarChart
                                data={chartRows}
                                layout="vertical"
                                margin={{ top: 1, right: chartMarginRight, left: 10, bottom: 20 }}
                                stackOffset="none"
                            >
                                <CartesianGrid horizontal strokeDasharray="3 3" vertical={false} />

                                <YAxis
                                    dataKey="bin"
                                    type="category"
                                    width={60}
                                    tick={{ fontSize: 12 }}
                                    axisLine={false}
                                    tickLine={false}
                                    tickMargin={10}
                                />

                                <XAxis
                                    type="number"
                                    hide
                                    domain={(showKg ? [0, (max: number) => Math.max(max, 0.01)] : [0, 100]) as any}
                                />

                                <Tooltip
                                    content={<CustomTooltip />}
                                    cursor={{ fill: "hsl(var(--foreground))", fillOpacity: 0.05, stroke: "none" }}
                                />

                                <Legend
                                    verticalAlign="bottom"
                                    iconType="square"
                                    iconSize={10}
                                    wrapperStyle={{ fontSize: 12, paddingTop: 14 }}
                                />

                                {/* Grey track, drawn FIRST so it sits behind the coloured bars.
                                    (A Bar's `background` is painted in JSX order: on the last bar it
                                    covered the other bars and washed their colours out.) */}
                                <Bar
                                    dataKey="__track"
                                    stackId="contamination"
                                    fill="transparent"
                                    legendType="none"
                                    background={{ fill: "hsl(var(--muted))", fillOpacity: 0.7 } as any}
                                    isAnimationActive={false}
                                />

                                {activeStreams.map((stream, idx) => (
                                    <Bar
                                        key={stream}
                                        dataKey={stream}
                                        stackId="contamination"
                                        fill={streamColors[stream]}
                                        name={chartConfig[stream].label}
                                        radius={
                                            idx === activeStreams.length - 1
                                                ? [0, 4, 4, 0]
                                                : [0, 0, 0, 0]
                                        }
                                    />
                                ))}

                                {/* Always rendered, zero-width, last in the stack: draws the
                                    full-width grey track behind every bin (so a bin with no
                                    contamination still shows a bar) and the total label. */}
                                <Bar
                                    dataKey="__track"
                                    stackId="contamination"
                                    fill="transparent"
                                    legendType="none"
                                    isAnimationActive={false}
                                >
                                    <LabelList
                                        dataKey={(entry: any) => {
                                            if (showKg) {
                                                const total = STREAMS.reduce(
                                                    (sum, s) => sum + (Number(entry[s]) || 0),
                                                    0
                                                )
                                                return `${total.toFixed(1)} kg`
                                            }
                                            return `${Number(entry.__contamination_rate || 0).toFixed(1)}%`
                                        }}
                                        position="right"
                                        offset={labelOffset}
                                        className="fill-foreground"
                                        fontSize={labelFontSize}
                                    />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartContainer>
                )}
            </div>
        </div>
    )
}